import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../store';
import {
    patchUserMusicalGenre, getMusicalGenresAvailable, createUserMusicalGenre,
} from '../../../../features/sessions/sessionProfileNavigation';
import { MusicGenre, UserMusicalGenre } from '../../../../#interfaces/interfaces';
import {
    Box, Chip, Dialog, DialogContent, DialogTitle, IconButton, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';

const BG_SECTION     = '#1c1c24';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

interface AddRemoveUserMusicalGenresProps {
    userNickname: string;
    onClose: (v: boolean) => void;
}

export default function AddRemoveUserMusicalGenres({ userNickname, onClose }: AddRemoveUserMusicalGenresProps) {
    const authHeaders          = useSelector((state: RootState) => state.session.authHeaders);
    const currentUser          = useSelector((state: RootState) => state.session.currentUser);
    const userMusicalGenres    = useSelector((state: RootState) => state.sessionProfile.userMusicalGenres);
    const musicalGenresAvailable = useSelector((state: RootState) => state.sessionProfile.musicalGenresAvailable);
    const dispatch = useDispatch<AppDispatch>();

    useEffect(() => {
        if (authHeaders) {
            dispatch(getMusicalGenresAvailable({ authHeaders }));
        }
    }, []);

    async function handleSelect(musicGenreId: number) {
        if (!authHeaders || !userNickname || !currentUser?.nickname || !currentUser?.id) return;

        const existing = userMusicalGenres.find((g: UserMusicalGenre) => g.musicGenre.id === musicGenreId);

        if (existing?.selected === false) {
            await dispatch(patchUserMusicalGenre({
                headers: authHeaders,
                urlParams: { userNickname: currentUser.nickname },
                requestBodyData: { musicGenreId, selected: true },
            }));
        } else if (existing?.id === undefined || !existing?.selected) {
            await dispatch(createUserMusicalGenre({
                headers: authHeaders,
                urlParams: { currentUserNickname: userNickname },
                requestBodyData: { userId: currentUser.id, musicGenreId },
            }));
        }
    }

    async function handleDeselect(musicGenreId: number) {
        if (!authHeaders || !userNickname || !currentUser?.nickname || !currentUser?.id) return;

        const existing = userMusicalGenres.find((g: UserMusicalGenre) => g.musicGenre.id === musicGenreId);
        if (existing?.selected === true) {
            await dispatch(patchUserMusicalGenre({
                headers: authHeaders,
                urlParams: { userNickname },
                requestBodyData: { musicGenreId, selected: false },
            }));
        }
    }

    return (
        <Dialog
            open
            onClose={() => onClose(false)}
            fullWidth
            maxWidth='xs'
            PaperProps={{
                sx: {
                    bgcolor: BG_SECTION,
                    border: BORDER_SUBTLE,
                    borderRadius: '12px',
                    color: TEXT_PRIMARY,
                },
            }}
        >
            <DialogTitle sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                pb: 1, borderBottom: BORDER_SUBTLE,
            }}>
                <Box>
                    <Typography variant='h6' fontWeight={700} color={TEXT_PRIMARY}>Genre Preferences</Typography>
                    <Box sx={{ width: 32, height: 3, bgcolor: BRAND_RED, mt: 0.5 }} />
                </Box>
                <IconButton onClick={() => onClose(false)} size='small' sx={{ color: TEXT_SECONDARY, '&:hover': { color: TEXT_PRIMARY } }}>
                    <CloseIcon fontSize='small' />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {musicalGenresAvailable.map((genre: MusicGenre, i: number) => {
                        const isSelected = userMusicalGenres.some(
                            (g: UserMusicalGenre) => g.musicGenre.description === genre.description && g.selected === true,
                        );
                        return (
                            <Chip
                                key={i}
                                label={genre.description}
                                size='small'
                                icon={isSelected
                                    ? <CheckIcon sx={{ fontSize: 14, color: `${TEXT_PRIMARY} !important` }} />
                                    : <AddIcon sx={{ fontSize: 14, color: `${TEXT_SECONDARY} !important` }} />}
                                onClick={() => isSelected ? handleDeselect(genre.id) : handleSelect(genre.id)}
                                sx={{
                                    cursor: 'pointer',
                                    fontWeight: 500,
                                    ...(isSelected ? {
                                        bgcolor: BRAND_RED,
                                        color: TEXT_PRIMARY,
                                        border: `1px solid ${BRAND_RED}`,
                                        '&:hover': { bgcolor: '#a93226' },
                                    } : {
                                        bgcolor: 'rgba(255,255,255,0.06)',
                                        color: TEXT_SECONDARY,
                                        border: BORDER_SUBTLE,
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.12)', color: TEXT_PRIMARY },
                                    }),
                                }}
                            />
                        );
                    })}
                </Box>
            </DialogContent>
        </Dialog>
    );
}
